import {ReactNode} from "react";
import {Card, Col, Row} from "react-bootstrap";
import {Link} from "react-router";
import {ProductsApi} from "../../api/ProductsApi";
import {SiteConfigApi} from "../../api/SiteConfigApi";
import {ProductViewModel, SellableEdition} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {RichText} from "../../components/RichText";
import {CopyButton} from "../../components/CopyButton";
import {TrademarkNotices, trademarksIn} from "../../components/TrademarkNotices";
import {formatBytes, formatCalendarDate, formatPrice} from "../../components/format";
import {allFormats, hasRequirements, sortedPlatforms} from "../../components/requirements";
import {productPath} from "../../structuredData";
import {SITE_NAME} from "../../seo";
import {format, language, press, products as productMessages, rich} from "../../i18n";

const messages = press.page;
const facts = messages.products.facts;
const andList = new Intl.ListFormat(language, { type: 'conjunction' });

// The logo pack (scripts/build-logo-pack.mjs builds it with the site).
export const LOGO_PACK_PATH = '/press/tristar-logos.zip';
export const PRESS_CONTACT_PATH = '/press/contact';

// About Tristar as the site words it: what the page shows until an administrator writes their own (Settings).
export const DEFAULT_PRESS_ABOUT = messages.about.paragraphs.map(x => `<p>${x}</p>`).join('');

// The products whose press kit is on the page: published (the catalog only has those) and switched on.
export const pressProducts = (all: ProductViewModel[]) => all.filter(x => x.published && x.press?.published);

function Fact({label, children}: { label: string, children?: ReactNode }) {
    return children ? <><dt className="col-sm-4">{label}</dt><dd className="col-sm-8">{children}</dd></> : null;
}

// What a reviewer needs to get right: who makes it, what it costs, the version, where it runs and whether it can be
// tried, all from the catalog, so it can't fall out of step with the product's page.
function FactSheet({product}: { product: ProductViewModel }) {
    const price = product.prices.find(x => x.edition === SellableEdition) ?? product.prices[0];
    const requirements = hasRequirements(product.requirements) ? product.requirements : undefined;
    const platforms = requirements && sortedPlatforms(requirements).map(x =>
        x.minimumVersion ? `${productMessages.requirements.platforms[x.platform]} ${x.minimumVersion}` : productMessages.requirements.platforms[x.platform]);
    const formats = requirements && allFormats(requirements).map(x => productMessages.requirements.formatNames[x]);
    return (
        <dl className="row press-facts mb-0">
            <Fact label={facts.developer}>{SITE_NAME}</Fact>
            <Fact label={facts.price}>{price && formatPrice(price.amount, price.currency)}</Fact>
            <Fact label={facts.version}>
                {product.currentVersion && format(facts.versionValue, { version: product.currentVersion.version, date: formatCalendarDate(product.currentVersion.releasedOn) })}
            </Fact>
            <Fact label={facts.platforms}>{platforms?.length ? andList.format(platforms) : undefined}</Fact>
            <Fact label={facts.formats}>{formats?.length ? andList.format(formats) : undefined}</Fact>
            <Fact label={facts.trial}>{product.currentVersion && !product.beta ? facts.trialValue : undefined}</Fact>
            <Fact label={facts.demos}>
                {product.demos.length > 0 && rich(facts.demosValue, { link: text => <Link to={productPath(product.sku)}>{text}</Link> })}
            </Fact>
        </dl>
    );
}

function ProductPressKit({product}: { product: ProductViewModel }) {
    const kit = product.press!;
    const hero = product.images[0];
    const headingId = `press-product-${product.id}`;
    return (
        <section className="mb-5" aria-labelledby={headingId}>
            <Card>
                {hero && <Card.Img variant="top" src={hero.url} alt={hero.altText} width={hero.width} height={hero.height} className="press-hero" loading="lazy"/>}
                <Card.Body className="p-4">
                    <h3 id={headingId} className="h4">{product.name}</h3>
                    <ul className="list-inline mb-4">
                        <li className="list-inline-item me-3">
                            <Link to={productPath(product.sku)}><i className="bi bi-box-arrow-up-right me-1" aria-hidden="true"></i>{messages.products.productPage}</Link>
                        </li>
                        {kit.imagePack && (
                            <li className="list-inline-item me-3">
                                <a href={kit.imagePack.url} download>
                                    <i className="bi bi-file-earmark-zip me-1" aria-hidden="true"></i>
                                    {format(messages.products.imagePack, { size: formatBytes(kit.imagePack.fileSize) })}
                                </a>
                                <span className="small text-body-secondary ms-2">{format(messages.products.imagePackHelp, { count: kit.imagePack.imageCount })}</span>
                            </li>
                        )}
                        {kit.reviewerNotes && (
                            <li className="list-inline-item">
                                <a href={kit.reviewerNotes.url} target="_blank" rel="noopener">
                                    <i className="bi bi-file-earmark-pdf me-1" aria-hidden="true"></i>
                                    {format(messages.products.reviewerNotes, { size: formatBytes(kit.reviewerNotes.fileSize) })}
                                </a>
                            </li>
                        )}
                    </ul>
                    <Row className="g-4">
                        {kit.summary && (
                            <Col lg={7}>
                                <h4 className="h5">{messages.products.features}</h4>
                                <RichText html={kit.summary}/>
                            </Col>
                        )}
                        <Col lg={kit.summary ? 5 : 12}>
                            <h4 className="h5">{messages.products.factSheet}</h4>
                            <FactSheet product={product}/>
                        </Col>
                    </Row>
                    <TrademarkNotices marks={trademarksIn(kit.summary, product.description)} className="mt-4"/>
                </Card.Body>
            </Card>
        </section>
    );
}

// The press kit, after gforcesoftware.com/about/press: who we are, logos, a kit for each product whose press kit is
// switched on (Administration > Products), boilerplate to copy, and how to reach us (/press/contact: a press inquiry,
// which can ask for a review license). No email address: inquiries go through the form. Prerendered for search engines.
export function PressPage() {
    const {data: all, error, loading} = useAsync(() => ProductsApi.getPublished(), []);
    // Without the config, the site's own wording.
    const {data: config, loading: configLoading} = useAsync(() => SiteConfigApi.get().catch(() => undefined), []);
    const about = config?.pressAbout?.trim() || DEFAULT_PRESS_ABOUT;
    const shown = all ? pressProducts(all) : [];

    return (
        <div className="press-page">
            <PageHeader title={messages.title} description={messages.description} path="/press"/>
            <p className="lead">{rich(messages.intro, { link: text => <Link to={PRESS_CONTACT_PATH}>{text}</Link> })}</p>

            <section className="mt-5" aria-labelledby="press-about">
                <h2 id="press-about" className="h4">{messages.about.title}</h2>
                {!configLoading && <RichText html={about}/>}
            </section>

            <section className="mt-5" aria-labelledby="press-logos">
                <h2 id="press-logos" className="h4">{messages.logos.title}</h2>
                <p>{messages.logos.contents}</p>
                <a href={LOGO_PACK_PATH} download className="btn btn-outline-primary mb-3">
                    <i className="bi bi-download me-2" aria-hidden="true"></i>{messages.logos.download}
                </a>
                <h3 className="h6">{messages.logos.usageTitle}</h3>
                <ul>{messages.logos.usage.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>

            <section className="mt-5" aria-labelledby="press-products">
                <h2 id="press-products" className="h4 mb-3">{messages.products.title}</h2>
                <ErrorAlert error={error}/>
                {loading && !all && <LoadingSpinner/>}
                {all && !shown.length && <p className="text-body-secondary">{messages.products.none}</p>}
                {shown.map(x => <ProductPressKit key={x.id} product={x}/>)}
            </section>

            <section className="mt-5" aria-labelledby="press-boilerplate">
                <h2 id="press-boilerplate" className="h4">{messages.boilerplate.title}</h2>
                <p>{messages.boilerplate.intro}</p>
                <blockquote className="border-start border-3 ps-3 mb-2">{messages.boilerplate.text}</blockquote>
                <CopyButton text={messages.boilerplate.text} label={messages.boilerplate.copy}/>
            </section>

            <section className="mt-5 mb-4" aria-labelledby="press-contact">
                <h2 id="press-contact" className="h4">{messages.contact.title}</h2>
                <p>{messages.contact.text}</p>
                <Link to={PRESS_CONTACT_PATH} className="btn btn-primary">
                    <i className="bi bi-envelope me-2" aria-hidden="true"></i>{messages.contact.button}
                </Link>
            </section>
        </div>
    );
}
